'use client'

import { Cell, InputText, Spacer, Text, Validation } from '@vinted/web-ui'
import { useCallback, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useDebounce } from '@marketplace-web/shared/ui-helpers'
import { useFormValidationMessage } from '@marketplace-web/vendor-abstractions/react-hook-form-feature'
import {
  EscrowFees,
  getPriceEstimateWithFees,
  transformPriceEstimateWithFees,
} from '@marketplace-web/escrow-pricing/escrow-pricing-data'
import { CurrencyAmountModel, formatCurrencyAmount } from '@marketplace-web/currency/currency-data'

import { OfferFormModel } from '../types'
import { OfferFormField } from '../constants'
import { withMemoizedPromise } from '../../utils/promise'

type Props = {
  showTitle?: boolean
  maxDiscount?: string
  minItemPrice: CurrencyAmountModel | undefined
  maxItemPrice: CurrencyAmountModel | undefined
  currentItemPrice: CurrencyAmountModel
  itemId: number
  orderItemIds?: Array<number>
}

const PRICE_PATTERN = /^[0-9]*((\.|,)[0-9]{0,2})?$/

export const OfferPriceField = ({
  minItemPrice,
  maxItemPrice,
  currentItemPrice,
  showTitle = true,
  itemId,
  orderItemIds,
}: Props) => {
  const {
    register,
    setValue,
    setFocus,
    formState: { errors, dirtyFields },
    subscribe,
  } = useFormContext<OfferFormModel>()

  // eslint-disable-next-line react/hook-use-state
  const [cache] = useState(() => new Map())

  const { fetch: fetchPriceEstimateWithFees, transformedData: priceEstimate } = useFetch(
    withMemoizedPromise(getPriceEstimateWithFees, { cache, cacheDuration: Infinity }),
    transformPriceEstimateWithFees,
  )

  const { locale } = useIntl()
  const translationPrefix = 'conversation.hero_message.make_offer'
  const translate = useTranslate(translationPrefix)
  const validationMessage = useFormValidationMessage(errors, `${translationPrefix}.offer_modal`)

  const getItemIds = useCallback(
    () => (orderItemIds ? orderItemIds.map(String) : [String(itemId)]),
    [orderItemIds, itemId],
  )
  const DEBOUNCE_DELAY = 600
  const getTotalPriceEstimate = useDebounce(
    value => {
      fetchPriceEstimateWithFees({
        item_ids: getItemIds(),
        offer_price: {
          amount: value as string,
          currency_code: currentItemPrice.currencyCode,
        },
        fees: [EscrowFees.BuyerProtection],
      })
    },
    DEBOUNCE_DELAY,
    false,
  )

  useEffect(() => {
    setValue(OfferFormField.Offer, '')

    fetchPriceEstimateWithFees({
      item_ids: getItemIds(),
      offer_price: {
        amount: currentItemPrice.amount,
        currency_code: currentItemPrice.currencyCode,
      },
      fees: [EscrowFees.BuyerProtection],
    })
  }, [
    setValue,
    currentItemPrice.amount,
    currentItemPrice.currencyCode,
    fetchPriceEstimateWithFees,
    getItemIds,
  ])

  useEffect(() => {
    setFocus(OfferFormField.Offer)
  }, [setFocus])

  useEffect(
    () =>
      subscribe({
        name: OfferFormField.Offer,
        formState: {
          values: true,
          errors: true,
          dirtyFields: true,
        },
        callback: data => {
          if (data.errors && OfferFormField.Offer in data.errors) return

          getTotalPriceEstimate(data.values[OfferFormField.Offer] || currentItemPrice.amount)
        },
      }),
    [subscribe, getTotalPriceEstimate, currentItemPrice.amount],
  )

  useEffect(() => {
    if (!dirtyFields[OfferFormField.Offer]) {
      setValue(OfferFormField.Offer, '')
    }
  }, [dirtyFields, setValue])

  const getMinItemPriceMessage = (minPrice: CurrencyAmountModel) => {
    const formattedMinItemPrice = formatCurrencyAmount(minPrice, locale)

    return translate('offer_modal.offer.errors.value_too_low', {
      value: formattedMinItemPrice,
    })
  }

  const min = minItemPrice
    ? {
        min: {
          value: minItemPrice.amount,
          message: getMinItemPriceMessage(minItemPrice),
        },
      }
    : {}

  const max = maxItemPrice
    ? {
        max: {
          value: maxItemPrice.amount,
          message: translate('offer_modal.offer.errors.value_too_high', {
            value: formatCurrencyAmount(maxItemPrice, locale),
          }),
        },
      }
    : {}

  const getInputValidationDefaultText = () => {
    const totalCombinedPrice = priceEstimate?.totalPrice

    return (
      <Text
        as="h4"
        testId="total-combined-price"
        type={Text.Type.Caption}
        text={
          totalCombinedPrice &&
          translate('offer_modal.total_combined_price', {
            price: formatCurrencyAmount(totalCombinedPrice, locale),
          })
        }
      />
    )
  }

  const getInputValidation = () => {
    const defaultText = getInputValidationDefaultText()

    return (
      <Validation text={validationMessage(OfferFormField.Offer) || defaultText} theme="warning" />
    )
  }

  return (
    <Cell>
      {showTitle && (
        <>
          <Text
            as="h3"
            type={Text.Type.Subtitle}
            text={translate('offer_modal.subtitle')}
            testId="offer-price-field-title"
          />
          <Spacer size={Spacer.Size.Small} />
        </>
      )}
      <InputText
        {...register(OfferFormField.Offer, {
          ...min,
          ...max,
          required: {
            value: true,
            message: translate('offer_modal.offer.errors.required'),
          },
          pattern: PRICE_PATTERN,
        })}
        placeholder={formatCurrencyAmount(currentItemPrice, locale)}
        inputMode={InputText.InputMode.Decimal}
        type={InputText.Type.Number}
        styling={InputText.Styling.Tight}
        validation={getInputValidation()}
        step={0.01}
        testId="offer-price-field"
      />
    </Cell>
  )
}

export default OfferPriceField
